import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useT } from '@/i18n';
import { ApiError } from '@/lib/api-client';
import { toast } from '@/stores/toast.store';
import { clientsApi, type ClientProfile } from './clients.api';
import { QUEUE_KEY } from '@/features/queue/useQueue';

/**
 * v0.6.1 Fase 9 — Cluster ED Phase 1: agent/admin edit client address +
 * phones from PreCallPage or ClientProfilePage. PHI-safe (server uses
 * encryption.withPhi), audit-logged (EDIT_CLIENT), and re-triggers
 * eligibility recompute when postalCode changes.
 *
 * Visual + behavior mirrors EditHouseholdMemberModal:
 *   - framer-motion AnimatePresence z-60 backdrop + centered panel
 *   - ESC + backdrop click close, focus trap on the panel
 *   - body-scroll-lock while open
 *   - Required `reason` (min 5 chars) before submit
 *   - On success: toast + ELIGIBILITY message (newly eligible / still
 *     ineligible) so the agent gets immediate feedback
 *
 * Accepts a minimal structural client shape so the modal works from
 * BOTH PreCallPage (NextClientResponse.client + clientId sibling) and
 * ClientProfilePage (full ClientProfile with id baked in).
 */
export interface EditClientInfoModalProps {
  client: {
    id: string;
    firstName: string;
    lastName: string;
    addressLine1: string | null;
    city: string | null;
    postalCode: string | null;
    isBronxZip: boolean;
    phoneHome: string | null;
    phoneCell: string | null;
  };
  open: boolean;
  onClose: () => void;
  /**
   * v0.6.1 Fase 9.1 — optional callback fired after a successful save with
   * the updated ClientProfile. Pages that use a non-TanStack store
   * (e.g. PreCallPage's Zustand active-call store) wire this to patch
   * their local state, since query-invalidation alone won't refresh them.
   */
  onSaved?: (updated: ClientProfile) => void;
}

export function EditClientInfoModal({
  client,
  open,
  onClose,
  onSaved,
}: EditClientInfoModalProps): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Form state seeded from current client values.
  const [address1, setAddress1] = useState<string>(client.addressLine1 ?? '');
  const [city, setCity] = useState<string>(client.city ?? '');
  const [postalCode, setPostalCode] = useState<string>(client.postalCode ?? '');
  const [phoneHome, setPhoneHome] = useState<string>(client.phoneHome ?? '');
  const [phoneCell, setPhoneCell] = useState<string>(client.phoneCell ?? '');
  const [reason, setReason] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);
  // Track last result so we can render the eligibility message inline.
  const [resultProfile, setResultProfile] = useState<ClientProfile | null>(null);

  // Re-seed state when the client prop changes (e.g. modal opened on a
  // different client without unmounting).
  useEffect(() => {
    if (!open) return;
    setAddress1(client.addressLine1 ?? '');
    setCity(client.city ?? '');
    setPostalCode(client.postalCode ?? '');
    setPhoneHome(client.phoneHome ?? '');
    setPhoneCell(client.phoneCell ?? '');
    setReason('');
    setFormError(null);
    setResultProfile(null);
  }, [open, client.id, client.addressLine1, client.city, client.postalCode, client.phoneHome, client.phoneCell]);

  // ESC + body-scroll-lock.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const mut = useMutation({
    mutationFn: (body: {
      address1?: string;
      city?: string;
      postalCode?: string;
      phoneHome?: string;
      phoneCell?: string;
      reason: string;
    }) => clientsApi.editInfo(client.id, body),
    onSuccess: (updated) => {
      setResultProfile(updated);
      // v0.6.1 Fase 9.1 — fixed query key + broader invalidation set so
      // ClientProfilePage (useClientProfile uses ['clients.profile', id])
      // and AgentQueue / PreCall caches all refetch immediately. PreCall
      // uses a Zustand store separate from TanStack — onSaved() callback
      // below lets PreCallPage patch it directly. Toast first, then close
      // the modal so the eligibility outcome remains visible briefly via
      // the toast (the inline success alert disappears with the modal).
      void qc.invalidateQueries({ queryKey: ['clients.profile', client.id] });
      // v0.6.1 Fase 10 — fixed phantom key (was ['agents.me.queue'];
      // canonical is QUEUE_KEY=['queue','me'] per useQueue.ts). Same
      // class as v0.3.0 hotfix v2; introduced during Fase 9.1.
      void qc.invalidateQueries({ queryKey: QUEUE_KEY });
      void qc.invalidateQueries({ queryKey: ['agents', 'dashboard'] });
      toast.success(
        t('editClientInfo.toast.successTitle'),
        t('editClientInfo.toast.successBody'),
      );
      // Fire optional caller callback (e.g. PreCallPage updates Zustand).
      if (onSaved) onSaved(updated);
      // Auto-close so the user doesn't have to dismiss manually.
      onClose();
    },
  });

  // Build a diff body — only send the fields the agent actually changed.
  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    const reasonTrim = reason.trim();
    if (reasonTrim.length < 5) {
      setFormError(t('editClientInfo.errorReasonRequired'));
      return;
    }
    if (postalCode && !/^\d{5}$/.test(postalCode.trim())) {
      setFormError(t('editClientInfo.errorPostalCodeFormat'));
      return;
    }

    const body: Parameters<typeof clientsApi.editInfo>[1] = { reason: reasonTrim };
    const trim = (s: string): string => s.trim();
    if (trim(address1) !== (client.addressLine1 ?? '')) body.address1 = trim(address1);
    if (trim(city) !== (client.city ?? '')) body.city = trim(city);
    if (trim(postalCode) !== (client.postalCode ?? '')) body.postalCode = trim(postalCode);
    if (trim(phoneHome) !== (client.phoneHome ?? '')) body.phoneHome = trim(phoneHome);
    if (trim(phoneCell) !== (client.phoneCell ?? '')) body.phoneCell = trim(phoneCell);

    const changedCount = Object.keys(body).filter((k) => k !== 'reason').length;
    if (changedCount === 0) {
      setFormError(t('editClientInfo.errorNoChanges'));
      return;
    }

    mut.mutate(body);
  };

  // Derive eligibility delta for the inline message.
  const eligibilityFeedback = (() => {
    if (!resultProfile) return null;
    const becameEligible = !client.isBronxZip && resultProfile.isBronxZip;
    const stillIneligible = !resultProfile.isBronxZip;
    return { becameEligible, stillIneligible };
  })();

  // B-04: createPortal a document.body, como DeclineModal / IneligibilityModal.
  // Sin el portal el modal se monta dentro del árbol de la página que lo abre;
  // PreCallPage tiene `animate-fade-up-slow` en su div raíz y esa animación
  // corre con animation-fill-mode:both, así que el `transform: translateY(0)`
  // del keyframe final queda pegado para siempre en la raíz. Un transform en
  // un ancestro crea containing block, el `fixed` del backdrop y del panel
  // pasan a anclarse a ESE div en vez de al viewport, y el modal terminaba
  // fuera de pantalla: no se podía llegar al botón de guardar.
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            className="fixed inset-0 z-[60] bg-ink-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            key="panel"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('editClientInfo.title')}
            tabIndex={-1}
            className="fixed inset-0 z-[61] flex items-center justify-center p-4 outline-none"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => {
              // Backdrop area clicks fall through to the outer backdrop; the
              // inner panel stopPropagation prevents accidental dismissal.
              if (e.target === e.currentTarget) onClose();
            }}
          >
            <div
              className="w-full max-w-xl rounded-lg bg-surface border border-ink-200 shadow-xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
                <div>
                  <h2 className="text-sm font-semibold text-ink-900">
                    {t('editClientInfo.title')}
                  </h2>
                  <p className="text-xs text-ink-500">
                    {client.firstName} {client.lastName}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('editClientInfo.close')}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-600 hover:bg-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lavender-400"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ec-address1">{t('editClientInfo.address1Label')}</Label>
                  <Input
                    id="ec-address1"
                    value={address1}
                    onChange={(e) => setAddress1(e.target.value)}
                    maxLength={200}
                    autoComplete="off"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="ec-city">{t('editClientInfo.cityLabel')}</Label>
                    <Input
                      id="ec-city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      maxLength={100}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ec-postal">{t('editClientInfo.postalCodeLabel')}</Label>
                    <Input
                      id="ec-postal"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      maxLength={5}
                      inputMode="numeric"
                      pattern="\d{5}"
                      placeholder="10455"
                      autoComplete="off"
                    />
                    <p className="text-[11px] text-ink-500">
                      {t('editClientInfo.postalCodeHelper')}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="ec-phone-cell">{t('editClientInfo.phoneCellLabel')}</Label>
                    <Input
                      id="ec-phone-cell"
                      value={phoneCell}
                      onChange={(e) => setPhoneCell(e.target.value)}
                      maxLength={30}
                      inputMode="tel"
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ec-phone-home">{t('editClientInfo.phoneHomeLabel')}</Label>
                    <Input
                      id="ec-phone-home"
                      value={phoneHome}
                      onChange={(e) => setPhoneHome(e.target.value)}
                      maxLength={30}
                      inputMode="tel"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ec-reason">{t('editClientInfo.reasonLabel')} *</Label>
                  <Textarea
                    id="ec-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={500}
                    minLength={5}
                    placeholder={t('editClientInfo.reasonPlaceholder')}
                    rows={2}
                  />
                  <p className="text-[11px] text-ink-500">
                    {t('editClientInfo.reasonHelper')}
                  </p>
                </div>

                {formError && (
                  <Alert variant="warning" role="alert">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                )}
                {mut.error && (
                  <Alert variant="error" role="alert">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{t('editClientInfo.errorSaveTitle')}</AlertTitle>
                    <AlertDescription>
                      {mut.error instanceof ApiError
                        ? mut.error.message
                        : (mut.error as Error).message}
                    </AlertDescription>
                  </Alert>
                )}

                {eligibilityFeedback?.becameEligible && (
                  <Alert variant="success" role="status">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>{t('editClientInfo.eligibilityBecameEligibleTitle')}</AlertTitle>
                    <AlertDescription>
                      {t('editClientInfo.eligibilityBecameEligibleBody')}
                    </AlertDescription>
                  </Alert>
                )}
                {eligibilityFeedback?.stillIneligible && (
                  <Alert variant="warning" role="status">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{t('editClientInfo.eligibilityStillIneligibleTitle')}</AlertTitle>
                    <AlertDescription>
                      {t('editClientInfo.eligibilityStillIneligibleBody')}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-ink-100">
                  <Button variant="ghost" type="button" onClick={onClose}>
                    {t('editClientInfo.cancel')}
                  </Button>
                  <Button type="submit" disabled={mut.isPending}>
                    {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t('editClientInfo.save')}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
